// Job Post Types and Data

export interface JobPost {
  slug: string;
  title: string;
  department: string;
  location: string;
  type: string;
  remote: boolean;
  publishDate: string;
  shortDescription: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
  niceToHave: string[];
  benefits: string[];
}

// Departments for filtering
export const departmentsDe = ["Alle Bereiche", "Sales", "Marketing", "Engineering", "Customer Success", "Product"] as const;
export const departmentsEn = ["All Departments", "Sales", "Marketing", "Engineering", "Customer Success", "Product"] as const;

export const getDepartments = (lang: string) => lang === "en" ? departmentsEn : departmentsDe;

export type Department = (typeof departmentsDe)[number] | (typeof departmentsEn)[number];

const jobPostsDe: JobPost[] = [
  {
    slug: "saas-sales-specialist",
    title: "SaaS Sales Specialist (m/w/d)",
    department: "Sales",
    location: "München / Remote",
    type: "Vollzeit",
    remote: true,
    publishDate: "2025-01-20",
    shortDescription: "Werde Teil unseres wachsenden Vertriebsteams und bringe unsere AI Agent Plattform zu mittelständischen Unternehmen.",
    description: `<p>Als SaaS Sales Specialist bei Chathero bist du verantwortlich für den gesamten Sales-Cycle – von der Lead-Qualifizierung bis zum erfolgreichen Abschluss. Du arbeitest eng mit Marketing und Customer Success zusammen, um unsere AI Agent Plattform an mittelständische Unternehmen zu verkaufen.</p><p>Du bist der erste Kontakt für potenzielle Kunden und zeigst ihnen, wie Chathero ihren Kundenservice, Marketing und Vertrieb revolutionieren kann.</p>`,
    responsibilities: [
      "Eigenständige Akquise und Qualifizierung von Leads im DACH-Markt",
      "Durchführung von Produktdemos und Präsentationen für Entscheider",
      "Entwicklung und Pflege einer starken Sales-Pipeline im CRM (HubSpot)",
      "Vertragsverhandlungen und Abschluss von SaaS-Deals",
      "Enge Zusammenarbeit mit Marketing bei Kampagnen und Events",
      "Kontinuierliche Weiterentwicklung unserer Sales-Prozesse",
      "Markt- und Wettbewerbsanalysen zur Identifikation von Chancen",
    ],
    requirements: [
      "3+ Jahre Erfahrung im B2B SaaS-Vertrieb, idealerweise im Mittelstandsumfeld",
      "Nachweisbare Erfolge beim Erreichen und Übertreffen von Umsatzzielen",
      "Erfahrung mit CRM-Systemen (HubSpot, Salesforce o.ä.)",
      "Ausgezeichnete Kommunikations- und Präsentationsfähigkeiten auf Deutsch",
      "Verständnis für KI, Chatbots oder Marketing-Automation von Vorteil",
      "Eigeninitiative, Durchsetzungsvermögen und hohe Abschlussorientierung",
      "Fließende Deutschkenntnisse, gute Englischkenntnisse",
    ],
    niceToHave: [
      "Erfahrung im Verkauf von AI- oder Automatisierungslösungen",
      "Netzwerk im Mittelstand oder bei Digitalagenturen",
      "Erfahrung mit Solution Selling oder MEDDIC-Methodik",
      "Technisches Grundverständnis für APIs und Integrationen",
    ],
    benefits: [
      "Attraktives Fixgehalt + unbegrenztes Provisionsmodell",
      "Flexible Arbeitszeiten und Remote-First-Kultur",
      "30 Tage Urlaub + zusätzliche freie Tage",
      "Modernste Arbeitsausstattung (MacBook, iPhone)",
      "Regelmäßige Team-Events und Offsites",
      "Weiterbildungsbudget für Kurse und Konferenzen",
      "Betriebliche Altersvorsorge",
      "Urban Sports Club Mitgliedschaft",
    ],
  },
  {
    slug: "enterprise-account-executive",
    title: "Enterprise Account Executive (m/w/d)",
    department: "Sales",
    location: "München / Remote",
    type: "Vollzeit",
    remote: true,
    publishDate: "2025-01-18",
    shortDescription: "Verantworte strategische Enterprise-Deals und baue langfristige Partnerschaften mit Großkunden auf.",
    description: `<p>Als Enterprise Account Executive bei Chathero bist du verantwortlich für die Gewinnung und Betreuung unserer größten Kunden. Du arbeitest mit komplexen Buying-Centern, entwickelst maßgeschneiderte Lösungen und baust langfristige strategische Partnerschaften auf.</p><p>Diese Rolle ist ideal für erfahrene Enterprise-Sales-Profis, die den Unterschied zwischen einem guten und einem großartigen Deal kennen.</p>`,
    responsibilities: [
      "Entwicklung und Umsetzung von Account-Strategien für Enterprise-Kunden",
      "Management komplexer Sales-Cycles mit mehreren Stakeholdern (6-12 Monate)",
      "Executive-Level Präsentationen und Verhandlungen (C-Level)",
      "Koordination von Cross-Functional Teams (Solution Engineering, Legal, Customer Success)",
      "Entwicklung von Business Cases und ROI-Analysen",
      "Forecast-Management und Pipeline-Reporting",
      "Identifikation von Upselling- und Cross-Selling-Möglichkeiten bei Bestandskunden",
    ],
    requirements: [
      "5+ Jahre Erfahrung im Enterprise SaaS-Vertrieb mit nachweisbaren Erfolgen",
      "Track Record bei Deals ab €100k ARR aufwärts",
      "Erfahrung in der Navigation komplexer Enterprise-Buying-Prozesse",
      "Starke Executive Presence und Verhandlungskompetenz",
      "Strategisches Denken und Business Acumen",
      "Erfahrung mit Enterprise-CRM und Sales-Tools",
      "Fließende Deutsch- und Englischkenntnisse",
    ],
    niceToHave: [
      "Erfahrung im Verkauf von AI-, CX- oder Marketing-Tech-Lösungen",
      "Bestehendes Netzwerk bei Enterprise-Kunden in DACH",
      "MBA oder vergleichbare Weiterbildung",
      "Erfahrung in Startup- oder Scale-up-Umfeld",
    ],
    benefits: [
      "Überdurchschnittliches OTE mit unlimitiertem Upside",
      "Equity-Beteiligung (VSOP)",
      "Flexible Arbeitszeiten und Remote-First-Kultur",
      "30 Tage Urlaub + zusätzliche freie Tage",
      "Premium-Arbeitsausstattung",
      "Großzügiges Weiterbildungsbudget",
      "Regelmäßige Team-Events und Offsites",
      "Betriebliche Altersvorsorge und weitere Benefits",
    ],
  },
];

const jobPostsEn: JobPost[] = [
  {
    slug: "saas-sales-specialist",
    title: "SaaS Sales Specialist (m/f/d)",
    department: "Sales",
    location: "Munich / Remote",
    type: "Full-time",
    remote: true,
    publishDate: "2025-01-20",
    shortDescription: "Join our growing sales team and bring our AI Agent platform to mid-sized businesses.",
    description: `<p>As a SaaS Sales Specialist at Chathero, you are responsible for the entire sales cycle – from lead qualification to successful closing. You work closely with Marketing and Customer Success to sell our AI Agent platform to mid-sized businesses.</p><p>You are the first point of contact for potential customers and show them how Chathero can revolutionize their customer service, marketing, and sales.</p>`,
    responsibilities: [
      "Independent acquisition and qualification of leads in the DACH market",
      "Conducting product demos and presentations for decision-makers",
      "Development and maintenance of a strong sales pipeline in CRM (HubSpot)",
      "Contract negotiations and closing SaaS deals",
      "Close collaboration with marketing on campaigns and events",
      "Continuous improvement of our sales processes",
      "Market and competitive analysis to identify opportunities",
    ],
    requirements: [
      "3+ years experience in B2B SaaS sales, ideally in the mid-market segment",
      "Proven track record of meeting and exceeding revenue targets",
      "Experience with CRM systems (HubSpot, Salesforce, etc.)",
      "Excellent communication and presentation skills in German",
      "Understanding of AI, chatbots, or marketing automation is a plus",
      "Self-initiative, assertiveness, and strong closing orientation",
      "Fluent German, good English skills",
    ],
    niceToHave: [
      "Experience selling AI or automation solutions",
      "Network in the mid-market or with digital agencies",
      "Experience with Solution Selling or MEDDIC methodology",
      "Basic technical understanding of APIs and integrations",
    ],
    benefits: [
      "Attractive base salary + unlimited commission model",
      "Flexible working hours and remote-first culture",
      "30 days vacation + additional days off",
      "Latest work equipment (MacBook, iPhone)",
      "Regular team events and offsites",
      "Learning & development budget for courses and conferences",
      "Company pension plan",
      "Urban Sports Club membership",
    ],
  },
  {
    slug: "enterprise-account-executive",
    title: "Enterprise Account Executive (m/f/d)",
    department: "Sales",
    location: "Munich / Remote",
    type: "Full-time",
    remote: true,
    publishDate: "2025-01-18",
    shortDescription: "Own strategic enterprise deals and build long-term partnerships with large clients.",
    description: `<p>As an Enterprise Account Executive at Chathero, you are responsible for acquiring and managing our largest customers. You work with complex buying centers, develop tailored solutions, and build long-term strategic partnerships.</p><p>This role is ideal for experienced enterprise sales professionals who know the difference between a good deal and a great one.</p>`,
    responsibilities: [
      "Development and execution of account strategies for enterprise customers",
      "Management of complex sales cycles with multiple stakeholders (6-12 months)",
      "Executive-level presentations and negotiations (C-Level)",
      "Coordination of cross-functional teams (Solution Engineering, Legal, Customer Success)",
      "Development of business cases and ROI analyses",
      "Forecast management and pipeline reporting",
      "Identification of upselling and cross-selling opportunities with existing customers",
    ],
    requirements: [
      "5+ years experience in enterprise SaaS sales with proven success",
      "Track record with deals of €100k+ ARR",
      "Experience navigating complex enterprise buying processes",
      "Strong executive presence and negotiation skills",
      "Strategic thinking and business acumen",
      "Experience with enterprise CRM and sales tools",
      "Fluent German and English",
    ],
    niceToHave: [
      "Experience selling AI, CX, or marketing tech solutions",
      "Existing network with enterprise clients in DACH",
      "MBA or equivalent qualification",
      "Experience in startup or scale-up environments",
    ],
    benefits: [
      "Above-average OTE with unlimited upside",
      "Equity participation (VSOP)",
      "Flexible working hours and remote-first culture",
      "30 days vacation + additional days off",
      "Premium work equipment",
      "Generous learning & development budget",
      "Regular team events and offsites",
      "Company pension plan and additional benefits",
    ],
  },
];

export const getJobPosts = (lang: string): JobPost[] => lang === "en" ? jobPostsEn : jobPostsDe;

// Legacy exports
export const jobPosts = jobPostsDe;
export const departments = departmentsDe;

export const getJobPostBySlug = (slug: string, lang?: string): JobPost | undefined => {
  const posts = lang ? getJobPosts(lang) : jobPostsDe;
  return posts.find((post) => post.slug === slug);
};

export const getJobPostsByDepartment = (department: string, lang?: string): JobPost[] => {
  const posts = lang ? getJobPosts(lang) : jobPostsDe;
  const allLabel = lang === "en" ? "All Departments" : "Alle Bereiche";
  if (department === allLabel) return posts;
  return posts.filter((post) => post.department === department);
};

export const formatJobDate = (dateString: string, lang?: string): string => {
  const date = new Date(dateString);
  const locale = lang === "en" ? "en-US" : "de-DE";
  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};
